/**
 * compressImage — resize and re-encode a File to a base64 JPEG data URL.
 *
 * Used by alert image uploads to keep MongoDB documents small. Defaults
 * cap the longest edge at 1280px and re-encode at 0.75 JPEG quality, which
 * produces ~150-250 KB images for typical phone photos. The output is
 * already a `data:image/jpeg;base64,...` URL ready to send to the backend.
 *
 * If the input file is smaller than `maxBytes` we still re-encode (canvas
 * round-trip strips EXIF, including GPS, before upload — privacy hygiene).
 */
const DEFAULT_MAX_EDGE = 1280;
const DEFAULT_QUALITY = 0.75;
const HARD_BASE64_CAP = 480_000; // safety net — matches backend MAX_ALERT_IMAGE_LEN

export async function compressImage(file, {
  maxEdge = DEFAULT_MAX_EDGE,
  quality = DEFAULT_QUALITY,
} = {}) {
  if (!file) throw new Error("No file provided");
  if (!file.type.startsWith("image/")) {
    throw new Error("Not an image file");
  }
  const dataUrl = await fileToDataUrl(file);
  const img = await loadImage(dataUrl);
  const { width, height } = scaleToFit(img.width, img.height, maxEdge);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; // PNG-with-alpha → white background; safer for JPEG
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  let out = canvas.toDataURL("image/jpeg", quality);
  // Step down quality if we still busted the cap (rare for 1280px).
  let q = quality;
  while (out.length > HARD_BASE64_CAP && q > 0.4) {
    q -= 0.1;
    out = canvas.toDataURL("image/jpeg", q);
  }
  if (out.length > HARD_BASE64_CAP) {
    throw new Error("Image is too large even after compression");
  }
  return out;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("FileReader failed"));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not decode image"));
    img.src = src;
  });
}

function scaleToFit(w, h, maxEdge) {
  if (w <= maxEdge && h <= maxEdge) return { width: w, height: h };
  const ratio = w >= h ? maxEdge / w : maxEdge / h;
  return {
    width: Math.round(w * ratio),
    height: Math.round(h * ratio),
  };
}
